import type { FieldValues, UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@apac/components/ui";
import {
  TextField,
  SelectField,
  type TextFieldProps,
  type SelectFieldProps,
} from "@apac/form-ui";
import {} from "@apac/form-ui";
import {
  APAC_TRAVELERS_NAME,
  ApacTravelersComponent,
  formattedFlyerOptions,
  type FrequentFlyerList,
} from "@apac/travelers";
import { useModuleBEM, useStyles } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { FrequentFlyerNumber } from "@hopper-b2b/types";
import { FrequentFlyerRow } from "./components/FrequentFlyerRow";
import defaultStyles from "./styles.module.scss";

export type FrequentFlyerFieldOverrides = {
  frequentFlyerNumber?: Partial<TextFieldProps<FieldValues>>;
  frequentFlyerProgram?: Partial<SelectFieldProps>;
};

interface FrequentFlyerDetailsProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  fieldOverrides: FrequentFlyerFieldOverrides;
  frequentFlyerList: FrequentFlyerList;
  onFrequentFlyerSubmit: (frequentFlyer: {
    frequentFlyerProgram: string;
    frequentFlyerNumber: FrequentFlyerNumber;
  }) => void;
  onRemoveFrequentFlyer: (trackingId: number) => void;
  subtitle?: string;
}

export const FrequentFlyerDetails: React.FC<FrequentFlyerDetailsProps> = ({
  formContext,
  frequentFlyerList,
  fieldOverrides: { frequentFlyerNumber = {}, frequentFlyerProgram = {} },
  onFrequentFlyerSubmit,
  onRemoveFrequentFlyer,
  subtitle,
}) => {
  const styles = useStyles(
    APAC_TRAVELERS_NAME,
    ApacTravelersComponent.FrequentFlyerDetails,
    defaultStyles
  );
  const [, cn] = useModuleBEM(
    styles,
    ApacTravelersComponent.FrequentFlyerDetails
  );
  const { t } = useI18nContext();

  const onSubmit = (e: React.FormEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const { frequentFlyerProgram, frequentFlyerNumber } =
      formContext.getValues();

    if (!frequentFlyerProgram || !frequentFlyerNumber) {
      return;
    }

    onFrequentFlyerSubmit({ frequentFlyerProgram, frequentFlyerNumber });

    formContext.resetField("frequentFlyerProgram");
    formContext.resetField("frequentFlyerNumber");
  };

  return (
    <Accordion defaultValue="frequent-flyer-details">
      <AccordionItem value="frequent-flyer-details">
        <AccordionTrigger className={cn("trigger")}>
          <div className={cn("title")}>
            {t("travelerForm.frequentFlyer.title")}
          </div>
        </AccordionTrigger>

        <AccordionContent>
          <div className={cn("container")}>
            {subtitle && <span className={cn("subtitle")}>{subtitle}</span>}
            <SelectField
              formContext={formContext}
              name={"frequentFlyerProgram"}
              label={t("frequentFlyerProgram")}
              placeholder={t("select")}
              options={formattedFlyerOptions}
              {...frequentFlyerProgram}
            />

            <div className={cn("frequent-flyer-number-wrapper")}>
              <TextField
                className={cn("frequent-flyer-number")}
                formContext={formContext}
                name={"frequentFlyerNumber"}
                placeholder={t("frequentFlyerNumber")}
                label={t("frequentFlyerNumber")}
                {...frequentFlyerNumber}
              />

              <AddMembershipButton
                formContext={formContext}
                className={cn("submit-button")}
                onClick={onSubmit}
                label={t("travelerForm.frequentFlyer.addMembership")}
              />
            </div>

            {!!frequentFlyerList?.length && (
              <div className={cn("frequent-flyer-list")}>
                {frequentFlyerList.map(({ key, value, trackingId }) => (
                  <FrequentFlyerRow
                    frequentFlyerNumber={value}
                    frequentFlyerProgramName={key}
                    onRemove={() => onRemoveFrequentFlyer(trackingId)}
                  />
                ))}
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const AddMembershipButton: React.FC<{
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  className?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
}> = ({ formContext, className, onClick, label }) => {
  // Subscribe this component to changes in the two fields
  useWatch({
    control: formContext.control,
    name: ["frequentFlyerProgram", "frequentFlyerNumber"],
  });

  const isProgramDirty = formContext.getFieldState(
    "frequentFlyerProgram"
  ).isDirty;
  const isNumberDirty = formContext.getFieldState(
    "frequentFlyerNumber"
  ).isDirty;

  return (
    <button
      className={className}
      onClick={onClick}
      disabled={!(isProgramDirty && isNumberDirty)}
    >
      {label}
    </button>
  );
};
